import { Alert, Button, Layout, Space, Tag } from 'antd';
import React, { useState } from 'react';

import {
    BackToModesLink,
    CORRECT_SCORE_THRESHOLD,
    Header,
    KRAJ_NAMES,
    MapFrame,
    ModalMapPreview,
    ModalPhoto,
    OptionButton,
    OptionGrid,
    Page,
    Question,
    QuizFinalResult,
    QuizPanel,
    QuizPhotoPreview,
    RoundResultModal,
    RoundStatus,
    SvgMap,
    calculateBlindMapScore,
    calculateDistanceKm,
    formatDistance,
    makeOptions,
    shuffleArray,
    useKrajFeatures,
} from '../../components/quiz';
import protectedAreasData from '../../data/protected-areas.json';

const { Content } = Layout;

const ITEMS = protectedAreasData.items.filter(
    item => Number.isFinite(item.lat) && Number.isFinite(item.lon) && item.kraj,
);
const TOTAL_ROUNDS = ITEMS.length;

const formatLabel = item =>
    `${item.kat === 'NP' ? 'NP' : 'CHKO'} ${item.nazev.replace('Krkonošský národní park', 'Krkonoše')}`;

const makeQuestion = item => {
    if (!item) return null;
    const type = Math.random() > 0.5 ? 'kraj' : 'map';

    if (type === 'kraj') {
        return {
            type: 'kraj',
            item,
            options: makeOptions(item.kraj, KRAJ_NAMES),
            acceptedAnswers: new Set(item.kraje && item.kraje.length ? item.kraje : [item.kraj]),
        };
    }

    return {
        type: 'map',
        item,
        target: { lat: item.lat, lon: item.lon },
    };
};

const ProtectedAreasGame = () => {
    const { features, error } = useKrajFeatures();
    const [questionQueue, setQuestionQueue] = useState(() => shuffleArray(ITEMS));
    const [round, setRound] = useState(1);
    const [score, setScore] = useState(0);
    const [question, setQuestion] = useState(() => makeQuestion(questionQueue[0]));
    const [selectedAnswer, setSelectedAnswer] = useState(null);
    const [guessPoint, setGuessPoint] = useState(null);
    const [mapResult, setMapResult] = useState(null);
    const [modalVisible, setModalVisible] = useState(false);
    const [rounds, setRounds] = useState([]);
    const [finished, setFinished] = useState(false);

    const maxScore = TOTAL_ROUNDS * 100;

    const restart = () => {
        const nextQueue = shuffleArray(ITEMS);
        setQuestionQueue(nextQueue);
        setRound(1);
        setScore(0);
        setQuestion(makeQuestion(nextQueue[0]));
        setSelectedAnswer(null);
        setGuessPoint(null);
        setMapResult(null);
        setModalVisible(false);
        setRounds([]);
        setFinished(false);
    };

    const item = question?.item;
    const itemLabel = item ? formatLabel(item) : '';
    const krajCorrect =
        question?.type === 'kraj' && selectedAnswer ? question.acceptedAnswers.has(selectedAnswer) : null;

    const answerKraj = option => {
        if (selectedAnswer) return;
        setSelectedAnswer(option);
        const correct = question.acceptedAnswers.has(option);
        if (correct) setScore(s => s + 100);
        setRounds(prev => [...prev, { correct, score: correct ? 100 : 0, label: itemLabel }]);
        setModalVisible(true);
    };

    const submitMapGuess = () => {
        if (!guessPoint || mapResult) return;
        const distance = calculateDistanceKm(guessPoint, question.target);
        const roundScore = calculateBlindMapScore(distance);
        setMapResult({ distance, score: roundScore });
        setScore(s => s + roundScore);
        setRounds(prev => [
            ...prev,
            { correct: roundScore >= CORRECT_SCORE_THRESHOLD, score: roundScore, label: itemLabel },
        ]);
        setModalVisible(true);
    };

    const advanceRound = () => {
        const nextRound = round + 1;
        setModalVisible(false);
        setRound(nextRound);
        setQuestion(makeQuestion(questionQueue[nextRound - 1]));
        setSelectedAnswer(null);
        setGuessPoint(null);
        setMapResult(null);
    };

    const showFinal = () => {
        setModalVisible(false);
        setFinished(true);
    };

    if (finished || !question) {
        return <QuizFinalResult score={score} totalRounds={TOTAL_ROUNDS} rounds={rounds} onRestart={restart} />;
    }

    const correctForModal =
        question.type === 'kraj' ? !!krajCorrect : mapResult ? mapResult.score >= CORRECT_SCORE_THRESHOLD : false;
    const wrongVerdictForModal =
        question.type === 'kraj' && selectedAnswer
            ? `${selectedAnswer} není správná odpověď :(`
            : question.type === 'map' && mapResult
            ? `${itemLabel} jsi netrefil :(`
            : null;

    return (
        <>
            <QuizPanel>
                <RoundStatus round={round} score={score} maxScore={maxScore} totalRounds={TOTAL_ROUNDS} />
                {question.type === 'kraj' ? (
                    <>
                        <Question>
                            <p>Velkoplošně chráněné území</p>
                            <h2>{itemLabel}</h2>
                            <p>Ve kterém kraji se z větší části nachází?</p>
                        </Question>
                        <QuizPhotoPreview item={item} alt={itemLabel} label="Fotka území" />
                        <OptionGrid>
                            {question.options.map(option => (
                                <OptionButton
                                    key={option}
                                    answer={option}
                                    correctAnswers={question.acceptedAnswers}
                                    disabled={!!selectedAnswer}
                                    selectedAnswer={selectedAnswer}
                                    onClick={() => answerKraj(option)}
                                />
                            ))}
                        </OptionGrid>
                    </>
                ) : (
                    <>
                        <Question>
                            <p>Slepá mapa chráněných území</p>
                            <h2>{itemLabel}</h2>
                            <p>Klikni do mapy přibližně tam, kde se nachází střed území.</p>
                        </Question>
                        <QuizPhotoPreview item={item} alt={itemLabel} label="Fotka území" />
                        {error && (
                            <Alert type="error" message="Mapa se nepodařila načíst" description={error.message} />
                        )}
                        <MapFrame>
                            {features ? (
                                <SvgMap
                                    features={features}
                                    guessPoint={guessPoint}
                                    targetPoint={mapResult ? question.target : null}
                                    onMapClick={mapResult ? null : setGuessPoint}
                                />
                            ) : (
                                <Alert type="info" message="Načítám mapu..." />
                            )}
                        </MapFrame>
                        {!mapResult && (
                            <Space wrap>
                                <Button type="primary" disabled={!guessPoint} onClick={submitMapGuess}>
                                    Vyhodnotit
                                </Button>
                                {guessPoint && <Tag>Tip umístěn</Tag>}
                            </Space>
                        )}
                    </>
                )}
            </QuizPanel>
            <RoundResultModal
                visible={modalVisible}
                correct={correctForModal}
                round={round}
                totalRounds={TOTAL_ROUNDS}
                onNext={advanceRound}
                onShowResult={showFinal}
                wrongVerdict={wrongVerdictForModal}
            >
                <h3>Správná odpověď</h3>
                <ModalPhoto item={item} alt={itemLabel} />
                <p>
                    <strong style={{ fontSize: '1.2em' }}>{itemLabel}</strong>
                </p>
                <p>
                    Rozloha {item.areaKm2} km².{' '}
                    {item.kraje && item.kraje.length > 1
                        ? `Zasahuje do krajů: ${item.kraje.join(', ')}.`
                        : `Leží v kraji: ${item.kraj}.`}
                </p>
                {question.type === 'map' && mapResult && (
                    <>
                        <p>
                            Tvůj tip byl <strong>{formatDistance(mapResult.distance)}</strong> od středu území ·{' '}
                            <strong>{mapResult.score}</strong> bodů za kolo.
                        </p>
                        <ModalMapPreview features={features} guessPoint={guessPoint} targetPoint={question.target} />
                    </>
                )}
                {question.type === 'kraj' && selectedAnswer && !krajCorrect && (
                    <p>
                        Tvůj tip byl: <strong>{selectedAnswer}</strong>.
                    </p>
                )}
            </RoundResultModal>
        </>
    );
};

export const ProtectedAreas = () => (
    <Content>
        <Page>
            <Header>
                <BackToModesLink />
                <h1>Národní parky a CHKO</h1>
                <p>
                    V každé hře projdeš všech {TOTAL_ROUNDS} národních parků a chráněných krajinných oblastí v Česku v
                    náhodném pořadí. Střídavě hádáš kraj nebo umisťuješ území na slepou mapu. Data: AOPK ČR (CC-BY 4.0).
                </p>
            </Header>
            <ProtectedAreasGame />
        </Page>
    </Content>
);
